import { BlueprintFamilyAdoptionMonthlyPoint, BlueprintFamilyTrendsMonthlyPoint, BlueprintFamilyTrendsPoint } from '../../../../api/core'
import { CatalogueMetricKey } from './catalogueUtils'

// The batched trends endpoints accept at most 10 ids per request.
export const MAX_TRENDS_SERIES = 10

// An adoption point. Every trends endpoint (`/{id}/trends`, `/trends/batch`,
// `/trends/event-groups`, `/trends/event-types`) is monthly and keys on `month`.
export type TrendsAdoptionPoint = BlueprintFamilyAdoptionMonthlyPoint

// The per-item shape every trends endpoint shares (per blueprint family, per
// event group, per event type, single family). Only the series the charts read.
export interface TrendsSeriesSource {
  adoptionRate?: TrendsAdoptionPoint[]
  density?: BlueprintFamilyTrendsMonthlyPoint[]
  activeLiveEventsCount?: BlueprintFamilyTrendsMonthlyPoint[]
}

// Which axis a series is on. Charts need this to know whether to bucket the points
// into months themselves (daily) or plot them as they arrive (already monthly).
export type TrendsGranularity = 'daily' | 'monthly'

export function trendsGranularityOf(points: readonly BlueprintFamilyTrendsPoint[] | undefined): TrendsGranularity {
  // Monthly is the safer default for an empty series: it plots points untouched,
  // whereas guessing "daily" would run month-bucketing over nothing and hide a bug.
  if (!points || points.length === 0) return 'monthly'
  return 'date' in points[0] ? 'daily' : 'monthly'
}

// The counts behind an adoption point, carried alongside the plotted share so a hover
// can say how many games it stands for. Both are optional because only the adoption
// lens has them.
export interface TrendsAdoptionCounts {
  // Cohort games that ran the entity on at least one day of the month, each counted
  // once — the numerator of the plotted share.
  distinctGamesCount?: number
  // The cohort: the denominator of the plotted share, the same on every point.
  trackedGamesCount?: number
}

// A plotted point plus whatever counts the lens it came from carries.
export type TrendsChartPoint = BlueprintFamilyTrendsPoint & TrendsAdoptionCounts

// The month's adoption share. The response carries only the two counts; an empty
// cohort is `0`, never NaN, matching the backend's own rates.
export function adoptionShareOf(point: Pick<TrendsAdoptionPoint, 'distinctGamesCount' | 'trackedGamesCount'>): number {
  return point.trackedGamesCount > 0 ? point.distinctGamesCount / point.trackedGamesCount : 0
}

// Map a trends item to the series of one metric lens. Adoption is each month's own
// share of the cohort; density is per adopting game, the same measure as the Density
// tile and the ranked list; concurrent instances is the activeLiveEventsCount series.
export function pickTrendsSeriesForMetric(item: TrendsSeriesSource | undefined, metric: CatalogueMetricKey): TrendsChartPoint[] {
  if (!item) return []
  if (metric === 'activeEvents') return item.activeLiveEventsCount ?? []
  if (metric === 'density') return item.density ?? []
  return (item.adoptionRate ?? []).map((point) => ({
    month: point.month,
    value: adoptionShareOf(point),
    distinctGamesCount: point.distinctGamesCount,
    trackedGamesCount: point.trackedGamesCount,
  }))
}

export type TrendsSeriesSourceWithDenominator = TrendsSeriesSource

export interface SeriesChange {
  first: number
  last: number
  delta: number
}

// First (baseline) and last (current) values of an ascending series.
//
// NOT the source for adoption change: use the momentum endpoint, which counts games
// rather than rounding shares. On the adoption series last − first matches it up to
// rounding.
export function seriesChange(series: readonly BlueprintFamilyTrendsPoint[] | undefined): SeriesChange | null {
  if (!series || series.length === 0) return null
  const first = series[0].value
  const last = series[series.length - 1].value
  return { first, last, delta: last - first }
}
